import React, { useMemo, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { connect, useSelector } from 'react-redux';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { useGetThresholdsQuery } from 'business-dashboard/src/api/thresholdsApi';
import CSVDownloadModal from 'commons/src/features/devicePage/dropdownOptions/CSVDownloadModal';
import { SensorTypes, ThresholdsResourceScope } from 'commons/src/models/commonEnums';
import { FullDeviceData } from 'commons/src/models/commonTypeScript';
import { SpaceThresholds } from 'commons/src/models/customThresholdModels';
import { ActionButton } from 'commons/src/models/menuModels';
import { Store } from 'commons/src/reducers';
import getThresholdValuesFromSpaceThresholds from 'commons/src/utils/thresholdUtils';
import CommonDeviceOverview from './deviceOverview/CommonDeviceOverview';
import { deviceActionButtons } from './DevicePageHeader';

type StateProps = {
    devicePageDevices: { [serialNumber: string]: FullDeviceData };
};

type Props = StateProps;

export const DeviceOverviewComponent = ({ devicePageDevices }: Props): React.ReactElement => {
    const { serialNumber } = useParams() as { serialNumber: string };
    const navigate = useNavigate();
    const outletContext = useOutletContext<import('./DevicePage').DevicePageOutletContext>() ?? {};
    const {
        showThresholdLines,
        setShowThresholdLines,
        scaleGraphToThresholds,
        setScaleGraphToThresholds,
        showLegendOnLeft,
        setShowLegendOnLeft,
        sensorLayoutKey = 0,
        resetSensorRowHeights,
    } = outletContext;
    const { selectedGroup } = useSelector((state: Store) => state.userSettings);

    const { data: thresholdData } = useGetThresholdsQuery(
        serialNumber && selectedGroup?.id
            ? { resourceId: serialNumber, scope: ThresholdsResourceScope.DEVICE }
            : skipToken
    );

    const apiThresholds = useMemo((): Record<string, number[]> | undefined => {
        if (!thresholdData || typeof thresholdData !== 'object') return undefined;
        const data = thresholdData as Record<string, SpaceThresholds>;
        const out = Object.keys(data).reduce<Record<string, number[]>>((acc, sensorType) => {
            const values = getThresholdValuesFromSpaceThresholds(data[sensorType], ThresholdsResourceScope.DEVICE);
            if (values.length) acc[sensorType] = values;
            return acc;
        }, {});
        return Object.keys(out).length ? out : undefined;
    }, [thresholdData]);

    const [isCSVModalOpen, setIsCSVModalOpen] = useState(false);
    const device: FullDeviceData = devicePageDevices[serialNumber] || {};

    const deviceWithRadonSensor =
        device && device.sensors && device.sensors.some(sensor => sensor.type === SensorTypes.radonShortTermAvg);

    const actionButtons: ActionButton[] = deviceActionButtons(
        (): void => setIsCSVModalOpen(true),
        deviceWithRadonSensor,
        navigate,
        serialNumber
    );

    return (
        <div className="single-device">
            <CommonDeviceOverview
                actionButtons={actionButtons}
                apiThresholds={apiThresholds}
                serialNumber={serialNumber}
                showThresholdLines={showThresholdLines}
                onShowThresholdLinesChange={setShowThresholdLines}
                scaleGraphToThresholds={scaleGraphToThresholds}
                onScaleGraphToThresholdsChange={setScaleGraphToThresholds}
                showLegendOnLeft={showLegendOnLeft}
                onShowLegendOnLeftChange={setShowLegendOnLeft}
                sensorLayoutKey={sensorLayoutKey}
                onResetSensorLayout={resetSensorRowHeights}
                topChildren={
                    isCSVModalOpen ? (
                        <CSVDownloadModal
                            onClose={(): void => setIsCSVModalOpen(false)}
                            segments={[
                                {
                                    serialNumber,
                                    deviceType: device.deviceType,
                                    segmentId: device.segmentId,
                                    segmentStartDate: device.segmentStart,
                                    name: device.segmentName,
                                },
                            ]}
                        />
                    ) : (
                        <div />
                    )
                }
            />
        </div>
    );
};

const mapStateToProps = (state: Store): StateProps => {
    const {
        devicePage: { devices: devicePageDevices },
    } = state;

    return {
        devicePageDevices,
    };
};

export default connect(mapStateToProps)(DeviceOverviewComponent);
